import { useState } from 'react'
import { Select } from 'src/360ui/Select'
import { Item } from '@react-stately/collections'
import { useAppIntl } from 'src/i18n'
import {
  addException,
  resetNftsExceptions,
  updateUrlWithNewSearchParameter,
  updateUrlWithoutSearchParameter,
} from 'src/utils/url'
import { useRouter } from 'next/router'
import { Switch } from 'src/360ui/Switch'
import useWidth from 'src/hooks/useWidth'
import { RangeFilters } from './components/RangeFilters'
import { CurrencySelect } from 'src/elements/CurrencySelect'
import {
  limitOptions,
  periodOptions,
  blockvalueOptions,
  blockchainOptions,
  blocksizeOptions,
} from './filtersOptions'
import { checkFilterExists } from 'src/utils/filters'
import { HideNftFilter } from './components/HideNftFilter'

export interface NFTFiltersProps {
  displayCurrency: string
  filters?: any
  min?: { [key: string]: any }
  max?: { [key: string]: any }
  isTable?: boolean
  data?: any
  exceptions?: any
  setExceptions?: any
  setDataFilter?: any
}

export const NFTFilters = ({
  displayCurrency,
  filters,
  min,
  max,
  isTable,
  data,
  exceptions,
  setExceptions,
  setDataFilter,
}: NFTFiltersProps) => {
  const intl = useAppIntl()
  const { isMobile } = useWidth()
  const router = useRouter()
  const onChangeFloorPrice = value => {
    !value
      ? updateUrlWithNewSearchParameter('floorprice', 'false')
      : updateUrlWithoutSearchParameter('floorprice')
    setFloorPrice(value)
  }
  const [floorPrice, setFloorPrice] = useState(true)
  const minValuesObj = !!min
    ? min
    : {
        volume: 0,
        marketCap: 0,
      }

  const maxValuesObj = !!max
    ? max
    : {
        volume: 0,
        marketCap: 0,
        floorPrice: 0,
        numberOfOwners: 0,
        numberOfItems: 0,
      }

  const defaultVolumeMin = router.query.filter_volume_min
    ? router.query.filter_volume_min
    : minValuesObj.volume

  const defaultVolumeMax = router.query.filter_volume_max
    ? router.query.filter_volume_max
    : maxValuesObj.volume

  const defaultMarketCapMin = router.query.filter_market_cap_min
    ? router.query.filter_market_cap_min
    : minValuesObj.marketCap

  const defaultMarketCapMax = router.query.filter_market_cap_max
    ? router.query.filter_market_cap_max
    : maxValuesObj.marketCap

  const defaultFloorPriceRangeMin = router.query.filter_floor_price_min
    ? router.query.filter_floor_price_min
    : 0

  const defaultFloorPriceRangeMax = router.query.filter_floor_price_max
    ? router.query.filter_floor_price_max
    : maxValuesObj.floorPrice

  const defaultNumberOfOwnersMin = router.query.filter_number_of_owners_min
    ? router.query.filter_number_of_owners_min
    : 1

  const defaultNumberOfOwnersMax = router.query.filter_number_of_owners_max
    ? router.query.filter_number_of_owners_max
    : maxValuesObj.numberOfOwners

  const defaultNumberOfAssetsMin = router.query.filter_number_of_items_min
    ? router.query.filter_number_of_items_min
    : 1

  const defaultNumberOfAssetsMax = router.query.filter_number_of_items_max
    ? router.query.filter_number_of_items_max
    : maxValuesObj.numberOfItems

  const blockvalueOptionsDecorated = blockvalueOptions.map(item =>
    item.value != 'floorPrice'
      ? item
      : { value: item.value, label: item.label + ' ' + displayCurrency }
  )

  return (
    <>
      {isMobile && <CurrencySelect />}
      {!isTable && (
        <Select
          label={intl.t('nft.filters.limit.label')}
          items={limitOptions}
          selectedKey={(filters.limit as string) || limitOptions[2].value}
          onSelectionChange={selected =>
            selected != '100'
              ? updateUrlWithNewSearchParameter('limit', selected as string)
              : updateUrlWithoutSearchParameter('limit')
          }
        >
          {item => <Item key={item.value}>{item.label}</Item>}
        </Select>
      )}

      <Select
        label={intl.t('nft.filters.period.label')}
        items={periodOptions}
        selectedKey={
          filters?.period
            ? checkFilterExists(filters.period, periodOptions) ||
              periodOptions[0].value
            : periodOptions[0].value
        }
        onSelectionChange={selected =>
          selected != '24h'
            ? updateUrlWithNewSearchParameter('period', selected as string)
            : updateUrlWithoutSearchParameter('period')
        }
      >
        {item => <Item key={item.value}>{item.label}</Item>}
      </Select>

      <Select
        label={intl.t('nft.filters.blockchain.label')}
        items={blockchainOptions}
        selectedKey={
          filters?.blockchain
            ? checkFilterExists(filters.blockchain, blockchainOptions) ||
              blockchainOptions[0].value
            : blockchainOptions[0].value
        }
        onSelectionChange={selected =>
          selected != 'all'
            ? updateUrlWithNewSearchParameter('blockchain', selected as string)
            : updateUrlWithoutSearchParameter('blockchain')
        }
      >
        {item => <Item key={item.value}>{item.label}</Item>}
      </Select>

      <Select
        label={intl.t('nft.filters.blocksize.label')}
        items={blocksizeOptions}
        selectedKey={(filters.dependsOn as string) || blocksizeOptions[0].value}
        onSelectionChange={selected =>
          updateUrlWithNewSearchParameter('dependsOn', selected as string)
        }
      >
        {item => <Item key={item.value}>{item.label}</Item>}
      </Select>

      <RangeFilters
        min={minValuesObj}
        max={maxValuesObj}
        defaultVolumeMin={defaultVolumeMin}
        defaultVolumeMax={defaultVolumeMax}
        defaultMarketCapMin={defaultMarketCapMin}
        defaultMarketCapMax={defaultMarketCapMax}
        defaultFloorPriceRangeMin={defaultFloorPriceRangeMin as number}
        defaultFloorPriceRangeMax={defaultFloorPriceRangeMax}
        defaultNumberOfOwnersMin={defaultNumberOfOwnersMin as number}
        defaultNumberOfOwnersMax={defaultNumberOfOwnersMax}
        defaultNumberOfAssetsMin={defaultNumberOfAssetsMin as number}
        defaultNumberOfAssetsMax={defaultNumberOfAssetsMax}
      />
      {!isTable && (
        <Select
          label={intl.t('nft.filters.blockvalue.label')}
          items={blockvalueOptionsDecorated}
          selectedKey={
            filters?.blockvalue
              ? checkFilterExists(
                  filters.blockvalue,
                  blockvalueOptionsDecorated
                ) || blockvalueOptionsDecorated[0].value
              : blockvalueOptionsDecorated[0].value
          }
          onSelectionChange={selected =>
            updateUrlWithNewSearchParameter('blockvalue', selected as string)
          }
        >
          {item => <Item key={item.value}>{item.label}</Item>}
        </Select>
      )}
      {!isMobile && !isTable && (
        <Switch isSelected={floorPrice} onChange={onChangeFloorPrice}>
          {intl.t('nft.filters.floorPrice')}
        </Switch>
      )}
      <HideNftFilter
        currentItemsDict={exceptions}
        addItem={data => addException(data)}
        removeItem={data => addException(data)}
        reset={() => resetNftsExceptions()}
        data={data}
        setExceptions={setExceptions}
        setDataFilter={setDataFilter}
      />
    </>
  )
}
